package com.narbase.letsgo.web.views.voiceassistant

import com.narbase.kunafa.core.components.View
import com.narbase.kunafa.core.components.layout.LinearLayout
import com.narbase.kunafa.core.components.textView
import com.narbase.kunafa.core.components.verticalLayout
import com.narbase.kunafa.core.css.*
import com.narbase.kunafa.core.dimensions.dependent.matchParent
import com.narbase.kunafa.core.dimensions.dependent.wrapContent
import com.narbase.kunafa.core.dimensions.px
import com.narbase.kunafa.core.dimensions.vw
import com.narbase.letsgo.dto.enum
import com.narbase.letsgo.dto.kmmLongOf
import com.narbase.letsgo.web.common.AppColors
import com.narbase.letsgo.web.dto.voiceassistant.VoiceAssistantModelReplyLogDto
import com.narbase.letsgo.web.dto.voiceassistant.VoiceAssistantSessionLogDto
import com.narbase.letsgo.web.dto.voiceassistant.VoiceAssistantShownResultLogDto
import com.narbase.letsgo.web.dto.voiceassistant.VoiceAssistantTranscriptionUsageLogDto
import com.narbase.letsgo.web.dto.voiceassistant.VoiceAssistantTurnLogDto
import com.narbase.letsgo.web.models.voiceassistant.VoiceAssistantConfidence
import com.narbase.letsgo.web.models.voiceassistant.VoiceAssistantEngine
import com.narbase.letsgo.web.models.voiceassistant.VoiceAssistantField
import com.narbase.letsgo.web.models.voiceassistant.VoiceAssistantOutcome
import com.narbase.letsgo.web.theme.main.MainTheme
import com.narbase.letsgo.web.theme.theme
import com.narbase.letsgo.web.translations.localized
import com.narbase.letsgo.web.utils.verticalFiller
import com.narbase.letsgo.web.utils.views.audio
import com.narbase.letsgo.web.utils.views.popUpDialog
import kotlin.math.roundToInt

class VoiceAssistantSessionDetailsDialog(private val viewModel: VoiceAssistantSessionsViewModel) {

    private val popUp = popUpDialog { }

    fun show(session: VoiceAssistantSessionLogDto) {
        theme.showDialog(popUp, customStyle = dialogStyle) {
            verticalLayout {
                style {
                    width = matchParent
                    padding = 24.px
                }
                sectionHeading("Session".localized())
                detailRow("Passenger".localized(), session.passengerName ?: session.passengerId.toString())
                detailRow("Engine".localized(), session.engine.enum<VoiceAssistantEngine>().name)
                detailRow("Outcome".localized(), session.outcome.enum<VoiceAssistantOutcome>().name)
                detailRow("Confidence".localized(), session.confidence.enum<VoiceAssistantConfidence>().name)
                detailRow("Selected place".localized(), session.selectedPointName ?: "N/A")
                session.transferReason?.let { detailRow("Transfer reason".localized(), it) }
                detailRow("Total time".localized(), session.totalDurationMillis.asReadableDuration())
                timingBreakdownRow(session.turns.toList(), session.totalDurationMillis.toLong())
                session.turns.flatMap { it.modelReplies.toList() }.reportedCostOrNull()?.let {
                    detailRow("Total cost".localized(), it.asCost())
                }
                detailRow(
                    "Ride".localized(),
                    session.rideId?.let { "#${it.toLong()}" } ?: "Not created".localized(),
                )
                session.rideStatus?.let { detailRow("Ride status".localized(), it) }

                verticalFiller(20.px)
                sectionHeading("Turns".localized())
                if (session.turns.isEmpty()) {
                    mutedText("This session has no recorded turns".localized())
                } else {
                    session.turns.forEachIndexed { index, turn -> turnCard(index + 1, turn) }
                }
            }
        }
    }

    fun showTurn(title: String, turn: VoiceAssistantTurnLogDto) {
        theme.showDialog(popUp, customStyle = dialogStyle) {
            verticalLayout {
                style {
                    width = matchParent
                    padding = 24.px
                }
                sectionHeading(title)
                turnCard(1, turn)
            }
        }
    }

    private fun LinearLayout.turnCard(turnNumber: Int, turn: VoiceAssistantTurnLogDto) {
        verticalLayout {
            style {
                width = matchParent
                marginBottom = 16.px
                padding = 14.px
                borderRadius = 8.px
                border = "1px solid ${AppColors.borderColor}"
            }

            textView {
                text = "$turnNumber. " + (if (turn.isRefused) "REFUSED - " else "") +
                        "${turn.field.enum<VoiceAssistantField>().name} - " +
                        "${turn.outcome.enum<VoiceAssistantOutcome>().name} - " +
                        "${turn.confidence.enum<VoiceAssistantConfidence>().name} - " +
                        turn.durationMillis.asReadableDuration()
                style {
                    width = matchParent
                    marginBottom = 10.px
                    fontSize = 14.px
                    fontWeight = "bold"
                }
            }

            detailRow("Chat model".localized(), turn.chatModel ?: "N/A")
            detailRow("Transcription model".localized(), turn.transcriptionModel ?: "N/A")
            usageRows(turn.modelReplies.toList())
            turn.transcriptionUsage?.let { transcriptionUsageRow(it) }
            turn.failureReason?.let { failure ->
                textView {
                    text = "Failure".localized() + ": " + failure
                    style {
                        width = matchParent
                        marginBottom = 4.px
                        fontSize = 14.px
                        color = AppColors.redDark
                        wordBreak = "break-word"
                    }
                }
            }

            verticalFiller(10.px)
            subHeading("Transcribed text".localized())
            textBox(turn.transcript)

            verticalFiller(10.px)
            subHeading("Recording".localized())
            recordingSection(turn)

            verticalFiller(10.px)
            subHeading("Place search results".localized())
            searchesSection(turn)

            verticalFiller(10.px)
            subHeading("Model replies".localized())
            modelRepliesSection(turn.modelReplies)

            verticalFiller(10.px)
            subHeading("Shown to the passenger".localized())
            shownResultSection(turn.shownResult)
        }
    }

    private fun LinearLayout.usageRows(replies: List<VoiceAssistantModelReplyLogDto>) {
        val promptTokens = replies.mapNotNull { it.promptTokens }
        val completionTokens = replies.mapNotNull { it.completionTokens }
        if (promptTokens.isNotEmpty() || completionTokens.isNotEmpty()) {
            detailRow(
                "Tokens".localized(),
                "${"in".localized()} ${promptTokens.sum()} - ${"out".localized()} ${completionTokens.sum()}",
            )
        }
        replies.reportedCostOrNull()?.let { detailRow("Cost".localized(), it.asCost()) }
    }

    private fun LinearLayout.timingBreakdownRow(turns: List<VoiceAssistantTurnLogDto>, totalMillis: Long) {
        if (totalMillis <= 0L) return
        val replies = turns.flatMap { it.modelReplies.toList() }
        val transcriptionMillis = turns.sumOf { it.transcriptionUsage?.durationMillis?.toLong() ?: 0L }
        val toolCallsMillis = replies.sumOf { reply -> reply.toolCalls.sumOf { it.durationMillis.toLong() } }
        val chatMillis = replies.sumOf { it.durationMillis.toLong() }
        val otherMillis = (totalMillis - transcriptionMillis - toolCallsMillis - chatMillis).coerceAtLeast(0L)

        fun share(label: String, millis: Long) =
            "$label: ${(millis * 100.0 / totalMillis).roundToInt()}% (${kmmLongOf(millis).asReadableDuration()})"

        detailRow(
            "Time split".localized(),
            listOf(
                share("Transcription".localized(), transcriptionMillis),
                share("Tool calls".localized(), toolCallsMillis),
                share("Chat response".localized(), chatMillis),
                share("Other".localized(), otherMillis),
            ).joinToString(" - "),
        )
    }

    private fun LinearLayout.transcriptionUsageRow(usage: VoiceAssistantTranscriptionUsageLogDto) {
        detailRow(
            "Transcription usage".localized(),
            listOfNotNull(
                usage.durationMillis.asReadableDuration(),
                usage.inputAudioTokens?.let { "${"audio in".localized()} $it" },
                usage.inputTextTokens?.let { "${"text in".localized()} $it" },
                usage.inputTokens?.takeIf { usage.inputAudioTokens == null }?.let { "${"in".localized()} $it" },
                usage.outputTokens?.let { "${"out".localized()} $it" },
                usage.audioSeconds?.let { "${it}s ${"audio".localized()}" },
            ).joinToString(" - "),
        )
    }

    private fun LinearLayout.modelRepliesSection(replies: Array<VoiceAssistantModelReplyLogDto>) {
        if (replies.isEmpty()) {
            mutedText("The chat model was not called for this turn".localized())
            return
        }
        replies.forEachIndexed { index, reply ->
            infoBox {
                boldLine(
                    listOfNotNull(
                        "${"Step".localized()} ${index + 1}",
                        reply.model,
                        reply.finishReason,
                        reply.durationMillis.asReadableDuration(),
                        reply.promptTokens?.let { "${"in".localized()} $it" },
                        reply.cachedPromptTokens?.let { "${"cached".localized()} $it" },
                        reply.completionTokens?.let { "${"out".localized()} $it" },
                        reply.reasoningTokens?.let { "${"reasoning".localized()} $it" },
                        reply.cost?.asCost(),
                    ).joinToString(" - ")
                )
                reply.toolCalls.forEach { toolCall ->
                    plainLine(
                        "→ ${toolCall.call} - ${toolCall.durationMillis.asReadableDuration()}"
                    )
                }
                reply.content?.takeIf { it.isNotBlank() }?.let { textBox(it) }
            }
        }
    }

    private fun LinearLayout.shownResultSection(shownResult: VoiceAssistantShownResultLogDto?) {
        if (shownResult == null) {
            mutedText("Not recorded for this turn".localized())
            return
        }
        infoBox {
            boldLine(shownResult.outcome.enum<VoiceAssistantOutcome>().name)
            shownResult.assistantMessage?.let { plainLine("${"Assistant message".localized()}: $it") }
            if (shownResult.fieldInterpretations.isEmpty()) plainLine("No place was offered".localized())
            shownResult.fieldInterpretations.forEach { fieldInterpretation ->
                boldLine(
                    "${fieldInterpretation.field.enum<VoiceAssistantField>().name} - " +
                            fieldInterpretation.confidence.enum<VoiceAssistantConfidence>().name
                )
                fieldInterpretation.candidates.forEachIndexed { index, candidate ->
                    plainLine(
                        "${index + 1}. ${candidate.title}" +
                                (candidate.subtitle?.let { " — $it" } ?: "") +
                                "  (${candidate.locationPoint.lat}, ${candidate.locationPoint.lng})"
                    )
                }
            }
        }
    }

    private fun List<VoiceAssistantModelReplyLogDto>.reportedCostOrNull() =
        mapNotNull { it.cost }.takeIf { it.isNotEmpty() }?.sum()

    private fun Double.asCost() = "$" + asDynamic().toFixed(6).unsafeCast<String>()

    private fun LinearLayout.infoBox(content: LinearLayout.() -> Unit) {
        verticalLayout {
            style {
                width = matchParent
                marginBottom = 8.px
                padding = 10.px
                borderRadius = 6.px
                backgroundColor = AppColors.extraLightBackground
            }
            content()
        }
    }

    private fun LinearLayout.boldLine(line: String) {
        textView {
            text = line
            style {
                width = matchParent
                marginTop = 4.px
                fontSize = 14.px
                fontWeight = "bold"
                wordBreak = "break-word"
            }
        }
    }

    private fun LinearLayout.plainLine(line: String) {
        textView {
            text = line
            style {
                width = matchParent
                marginTop = 4.px
                fontSize = 13.px
                wordBreak = "break-word"
            }
        }
    }

    private fun LinearLayout.recordingSection(turn: VoiceAssistantTurnLogDto) {
        if (turn.hasAudio.not()) {
            mutedText("No recording was stored for this turn".localized())
            return
        }
        val player = audio {
            controls = true
            style { width = matchParent }
        }
        viewModel.loadTurnAudio(turn.turnId) { dataUrl ->
            if (dataUrl == null) player.isVisible = false else player.src = dataUrl
        }
    }

    private fun LinearLayout.textBox(content: String) {
        textView {
            text = content.ifBlank { "N/A" }
            style {
                width = matchParent
                padding = 10.px
                borderRadius = 6.px
                backgroundColor = AppColors.extraLightBackground
                fontSize = 14.px
                wordBreak = "break-word"
                whiteSpace = "pre-wrap"
            }
        }
    }

    private fun LinearLayout.searchesSection(turn: VoiceAssistantTurnLogDto) {
        if (turn.searches.isEmpty()) {
            mutedText("No place search was run for this turn".localized())
            return
        }
        turn.searches.forEach { search ->
            infoBox {
                boldLine("\"${search.query}\"")
                if (search.results.isEmpty()) {
                    mutedText("No match".localized())
                } else {
                    search.results.forEach { result ->
                        plainLine(
                            "#${result.candidateId}  ${result.name}" +
                                    (result.address?.let { " — $it" } ?: "") +
                                    (result.area?.let { "  ·  ${"Area".localized()}: $it" } ?: "") +
                                    (result.district?.let { "  ·  ${"District".localized()}: $it" } ?: "") +
                                    (result.relevance?.let { "  ·  ${"Relevance".localized()}: $it" } ?: "") +
                                    "  (${result.lat}, ${result.lng})"
                        )
                    }
                }
            }
        }
    }

    private fun LinearLayout.sectionHeading(title: String) {
        textView {
            text = title
            style {
                width = wrapContent
                marginBottom = 8.px
                fontSize = 16.px
                fontWeight = "bold"
            }
        }
    }

    private fun LinearLayout.subHeading(title: String) {
        textView {
            text = title
            style {
                width = wrapContent
                marginBottom = 4.px
                fontSize = 13.px
                fontWeight = "bold"
                color = AppColors.textDarkGrey
            }
        }
    }

    private fun LinearLayout.detailRow(label: String, value: String) {
        textView {
            text = "$label: $value"
            style {
                width = matchParent
                marginBottom = 4.px
                fontSize = 14.px
                wordBreak = "break-word"
            }
        }
    }

    private fun LinearLayout.mutedText(message: String): View = textView {
        text = message
        style {
            width = matchParent
            fontSize = 13.px
            color = AppColors.textLight
        }
    }

    companion object {
        private val dialogStyle = MainTheme.ThemeDialogStyle(minWidth = 50.vw)
    }
}
